/** @jsx jsx */

import React, {
  Fragment as _,
  useState,
  useEffect,
  useContext,
  useMemo,
  useRef,
  ReactElement,
} from 'react';
import { css, jsx } from '@emotion/core';
import { interactionContext } from './interactionContext';
import shuffle from 'services/shuffle';
import { unlockInteraction } from 'services/unlockInteraction';
import EditorBtn from 'components/UI/EditorBtn';
import Layout from './Layout';
import { VisuallyHidden } from 'components/UI/VisuallyHidden';
import reRenderedFeedback from './ReRenderedFeedback';
import pubSub, { usePubSub } from 'services/pubSub';

const ResetText = () => {
  return (
    <_>
      <VisuallyHidden>Reset</VisuallyHidden>
      <svg
        width="75"
        height="16"
        viewBox="0 0 75 16"
        css={css`
          margin-top: 2px;
          margin-left: 7px;
        `}
      >
        <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
          <g transform="translate(-1063.000000, -799.000000)">
            <g transform="translate(1062.000000, 794.000000)">
              <path
                d="M7.752,9.568 C7.752,8.072 6.936,7.12 4.93,7.12 L2.55,7.12 L2.55,12.067 L4.947,12.067 C6.885,12.067 7.752,11.013 7.752,9.568 Z M1.36,6.117 L4.93,6.117 C7.684,6.117 8.976,7.613 8.976,9.568 C8.976,11.2 8.058,12.577 6.052,12.951 L9.112,18 L7.684,18 L4.743,13.036 L2.55,13.036 L2.55,18 L1.36,18 L1.36,6.117 Z M15.8,9.602 C14.117,9.602 12.638,10.69 12.502,12.849 L19.03,12.849 C19.081,10.69 17.517,9.602 15.8,9.602 Z M20.101,15.059 C19.676,16.81 18.18,18.136 15.851,18.136 C13.216,18.136 11.295,16.3 11.295,13.359 C11.295,10.401 13.182,8.582 15.851,8.582 C18.571,8.582 20.237,10.52 20.237,12.883 C20.237,13.257 20.237,13.495 20.203,13.818 L12.502,13.818 C12.604,15.994 14.117,17.133 15.851,17.133 C17.432,17.133 18.503,16.3 18.843,15.059 L20.101,15.059 Z M29.339,15.535 C29.339,17.031 28.03,18.136 26.109,18.136 C24.001,18.136 22.522,17.082 22.369,15.382 L23.593,15.382 C23.695,16.402 24.579,17.133 26.092,17.133 C27.418,17.133 28.166,16.419 28.166,15.535 C28.166,13.24 22.59,14.583 22.59,11.098 C22.59,9.721 23.899,8.582 25.854,8.582 C27.843,8.582 29.152,9.636 29.254,11.421 L28.064,11.421 C27.996,10.384 27.231,9.602 25.82,9.602 C24.579,9.602 23.797,10.265 23.797,11.081 C23.797,13.597 29.288,12.254 29.339,15.535 Z M36.044,9.602 C34.361,9.602 32.882,10.69 32.746,12.849 L39.274,12.849 C39.325,10.69 37.761,9.602 36.044,9.602 Z M40.345,15.059 C39.92,16.81 38.424,18.136 36.095,18.136 C33.46,18.136 31.539,16.3 31.539,13.359 C31.539,10.401 33.426,8.582 36.095,8.582 C38.815,8.582 40.481,10.52 40.481,12.883 C40.481,13.257 40.481,13.495 40.447,13.818 L32.746,13.818 C32.848,15.994 34.361,17.133 36.095,17.133 C37.676,17.133 38.747,16.3 39.087,15.059 L40.345,15.059 Z M43.497,15.484 L43.497,9.721 L42.205,9.721 L42.205,8.718 L43.497,8.718 L43.497,6.389 L44.704,6.389 L44.704,8.718 L47.254,8.718 L47.254,9.721 L44.704,9.721 L44.704,15.484 C44.704,16.623 45.095,16.98 46.166,16.98 L47.254,16.98 L47.254,18 L45.979,18 C44.364,18 43.497,17.337 43.497,15.484 Z"
                fill="#ffffff"
                fillRule="nonzero"
              ></path>
              <g transform="translate(60.000000, 5.000000)">
                <path
                  d="M7.5696,0.5 C11.4736,0.5 14.6396,3.665 14.6396,7.569 C14.6396,11.475 11.4736,14.64 7.5696,14.64 C3.6656,14.64 0.5006,11.475 0.5006,7.569 C0.5006,5.617 1.2916,3.85 2.5706,2.57"
                  stroke="#ffffff"
                ></path>
                <polygon
                  fill="#ffffff"
                  points="1.2679 1.291 3.8259 1.291 3.8259 3.843"
                ></polygon>
              </g>
            </g>
          </g>
        </g>
      </svg>
    </_>
  );
};

interface MatchingProps {
  children: ReactElement[] | ReactElement | string | null;
}

const Placeholder = () => {
  const context = useContext(interactionContext);

  const { panelWidth = 250, panelHeight = 250 } = context.props;

  return (
    <div
      css={css`
        width: ${panelWidth}px;
        height: ${panelHeight}px;
        margin: 10px;
        background: transparent;
        border: 2px solid transparent;
      `}
    />
  );
};

const Matching = (props: MatchingProps) => {
  const { children, reset } = props as any;
  const MatchDrags = children[0].props.children;
  const MatchDrops = children[1].props.children;
  const Feedbacks = children[2].props.children;

  const shuffledMatchDrags = useMemo(
    () => (!window.dev() ? shuffle(MatchDrags) : MatchDrags),
    []
  );

  const context: any = useContext(interactionContext);
  const [feedback, setFeedback] = useState(null);

  // console.log({ id: (props as any).id, contextId: context.id });
  // const [value, publish] = usePubSub(
  //   `MATCHING_INTERACTION_FEEDBACK_${context.id}`
  // );

  // // value && console.log({ value });

  // if (value) {
  //   !feedback && setFeedback(Feedbacks[value]);
  // }

  const itemsSeen = useRef(unlockInteraction(context.id, MatchDrags.length));

  const [columns, setColumns] = useState({
    column_a: shuffledMatchDrags,
    column_b: MatchDrops,
  });

  const onDragEnd = data => {
    if (!data.destination) {
      return;
    }

    const { draggableId, destination } = data;

    const { column_a, column_b } = columns;

    const dragIndex = column_a.findIndex(
      (drag: any) => `drag_${drag.props.draggableId}` === draggableId
    );
    const dropIndex = column_b.findIndex(
      (drop: any) =>
        `drop_${drop.props.droppableId}` === destination.droppableId
    );

    // if drag / drop don't match. return
    if (
      draggableId.replace('drag_', '') !==
      destination.droppableId.replace('drop_', '')
    )
      return;

    // add the drag to column_b
    const updatedColumnB = [...column_b];
    updatedColumnB[dropIndex] = column_a[dragIndex];

    // update columns
    const updatedColumns = {
      column_a: column_a.map((d, index) =>
        updatedColumnB.includes(d) ? (
          <Placeholder key={`placeholder_${index}`} />
        ) : (
          d
        )
      ),
      column_b: updatedColumnB.map((entry, index) =>
        index === dropIndex
          ? React.cloneElement(entry, {
              isDragDisabled: true,
            })
          : entry
      ),
    };

    setFeedback(Feedbacks[dropIndex]);
    setColumns(updatedColumns);
  };

  useEffect(() => {
    if (window.dev()) return;

    if (context.props.lockDown === 0) return;
    feedback && itemsSeen.current(feedback.props.id);
  });

  useEffect(() => {
    const pS = pubSub.subscribe(
      `MATCHING_INTERACTION_FEEDBACK_${context.id}`,
      ({ eventType, index }) => {
        // console.log({ index, feedback });
        switch (eventType) {
          // case 'mouseOver': {
          //   console.log({ eventType, index });
          //   return;
          // }

          // case 'mouseOut': {
          //   console.log({ eventType, index });
          //   return;
          // }

          case 'click': {
            setFeedback(Feedbacks[index]);
            return;
          }
        }
      }
    );

    return () => pS();
  }, []);

  return (
    <Layout
      columns={columns}
      onDragEnd={onDragEnd}
      feedback={reRenderedFeedback(feedback)}
      // feedback={feedback}
      useMediaInFeedback={context.props.useMediaInFeedback}
      setFeedback={setFeedback}
      reset={reset}
    />
  );
};

export const MatchingWrapper = ({ children, ...props }) => {
  const [reset, setReset] = useState(null);

  return (
    <_
      key={`Matching_${children[0].props.children.length}${reset &&
        `_${reset}`}`}
    >
      <Matching children={children} {...{ ...props, reset }} />
      {window.dev() && (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 60px;
          `}
        >
          <EditorBtn
            label={<ResetText />}
            onClick={() => setReset(Math.random())}
            css={css`
              width: 150px;
            `}
          />
        </div>
      )}
    </_>
  );
};

// export default Matching;
export default MatchingWrapper;
